import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Card, Checkbox, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import {
    CutoffAreaOutput,
    CutoffPlanningStatus,
    useCutoffQuery,
} from './graphql/queries/getCutoffTree';
import {
    EXECUTE_CUTOFF,
    ExecuteCutoffResponse,
    ExecuteCutoffVariables,
} from './graphql/mutations/executeCutoff';

interface CutoffTreeNode {
    key: string;
    title: string;
    isLeaf: boolean;
    areaId?: number;
    lastCutoffAt?: number | null;
    planningStatus?: CutoffPlanningStatus;
    children?: CutoffTreeNode[];
}

/**
 * 全部从 area.sortingCode 拆段建树（四段：仓/流向/路区/线路），不用 area.name。
 * sorting_code 形如 `ATL-CTL-A04`：前段=仓/流向分组；最后段 `A04` 再拆 字母(路区分组) + 数字(线路叶子)。
 * 末段无"字母+数字"结构时兜底：整段作为叶子；sorting_code 缺失 → 归"未分类"分组、叶子用 area.name。
 */
function buildTree(areas: CutoffAreaOutput[], unclassifiedLabel: string): CutoffTreeNode[] {
    const roots: CutoffTreeNode[] = [];
    const groupByPath = new Map<string, CutoffTreeNode>();

    // 沿分组路径创建/复用分组节点，返回最深分组的 children 数组（路径为空则返回 roots）
    const ensureGroupChildren = (groupSegments: string[]): CutoffTreeNode[] => {
        let siblings = roots;
        let path = '';
        for (const seg of groupSegments) {
            path = path ? `${path}-${seg}` : seg;
            let node = groupByPath.get(path);
            if (!node) {
                node = { key: `g:${path}`, title: seg, isLeaf: false, children: [] };
                groupByPath.set(path, node);
                siblings.push(node);
            }
            siblings = node.children!;
        }
        return siblings;
    };

    const sorted = [...areas].sort((a, b) => (a.sortingCode ?? '').localeCompare(b.sortingCode ?? ''));
    for (const area of sorted) {
        const raw = area.sortingCode?.trim();
        let groupSegments: string[];
        let leafLabel: string;
        if (!raw) {
            groupSegments = [unclassifiedLabel];
            leafLabel = area.name;
        } else {
            const segs = raw.split('-');
            const last = segs[segs.length - 1];
            const m = last.match(/^([A-Za-z]+)(\d+)$/); // 路区字母 + 线路数字
            if (m) {
                groupSegments = [...segs.slice(0, -1), m[1]];
                leafLabel = m[2];
            } else {
                groupSegments = segs.slice(0, -1);
                leafLabel = last;
            }
        }
        ensureGroupChildren(groupSegments).push({
            key: `a:${area.areaId}`,
            title: leafLabel,
            isLeaf: true,
            areaId: area.areaId,
            lastCutoffAt: area.lastCutoffAt,
            planningStatus: area.planningStatus,
        });
    }
    return roots;
}

const CutoffPage: React.FC = () => {
    const { t } = useTranslation();
    const { modal, message } = App.useApp();
    const { currentWarehouse } = useWarehouseContext();
    const hubId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;

    const { areas, loading, refetch } = useCutoffQuery(hubId);
    const [checkedLeafIds, setCheckedLeafIds] = useState<Set<number>>(new Set());
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [executeCutoff, { loading: executing }] = useMutation<ExecuteCutoffResponse, ExecuteCutoffVariables>(
        EXECUTE_CUTOFF
    );

    const treeData = useMemo(
        () => buildTree(areas, t('wms.cutoff.unclassified')),
        [areas, t]
    );
    const areaMap = useMemo(() => {
        const map = new Map<number, CutoffAreaOutput>();
        areas.forEach((a) => map.set(a.areaId, a));
        return map;
    }, [areas]);

    // 每个节点 key → 其下所有叶子线路 areaId（分组行取全部子孙叶子；叶子行取自身）
    const nodeLeafIds = useMemo(() => {
        const map = new Map<string, number[]>();
        const collect = (node: CutoffTreeNode): number[] => {
            const ids = node.isLeaf
                ? (node.areaId != null ? [node.areaId] : [])
                : (node.children ?? []).flatMap(collect);
            map.set(node.key, ids);
            return ids;
        };
        treeData.forEach(collect);
        return map;
    }, [treeData]);

    // 默认只展开 仓(hub,depth1) 一级，流向层保持收起；受控展开避免异步数据下 defaultExpandAllRows 失效
    const defaultExpandedKeys = useMemo(() => {
        const keys: string[] = [];
        const walk = (nodes: CutoffTreeNode[], depth: number) => {
            nodes.forEach((n) => {
                if (!n.isLeaf) {
                    if (depth <= 1) keys.push(n.key);
                    walk(n.children ?? [], depth + 1);
                }
            });
        };
        walk(treeData, 1);
        return keys;
    }, [treeData]);
    useEffect(() => setExpandedKeys(defaultExpandedKeys), [defaultExpandedKeys]);

    const allLeafIds = useMemo(() => areas.map((a) => a.areaId), [areas]);
    const selectedAreaIds = useMemo(() => Array.from(checkedLeafIds), [checkedLeafIds]);

    const toggleLeaves = (ids: number[], checked: boolean) => {
        setCheckedLeafIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
            return next;
        });
    };

    const renderPlanningStatus = (status?: CutoffPlanningStatus) => {
        switch (status) {
            case 'IN_PROGRESS':
                return <Tag color="processing">{t('wms.cutoff.status.inProgress')}</Tag>;
            case 'COMPLETED':
                return <Tag color="success">{t('wms.cutoff.status.completed')}</Tag>;
            case 'FAILED':
                return <Tag color="error">{t('wms.cutoff.status.failed')}</Tag>;
            default:
                return <span>--</span>;
        }
    };

    const allChecked = allLeafIds.length > 0 && allLeafIds.every((id) => checkedLeafIds.has(id));
    const someChecked = checkedLeafIds.size > 0;

    const columns: ColumnsType<CutoffTreeNode> = [
        {
            title: (
                <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked && !allChecked}
                    onChange={(e) => toggleLeaves(allLeafIds, e.target.checked)}
                >
                    {t('wms.cutoff.selectAll')}
                </Checkbox>
            ),
            dataIndex: 'title',
            key: 'title',
            width: 420,
            render: (_, record) => {
                const leafIds = nodeLeafIds.get(record.key) ?? [];
                const checkedCount = leafIds.filter((id) => checkedLeafIds.has(id)).length;
                return (
                    <Checkbox
                        checked={leafIds.length > 0 && checkedCount === leafIds.length}
                        indeterminate={checkedCount > 0 && checkedCount < leafIds.length}
                        disabled={leafIds.length === 0}
                        onChange={(e) => toggleLeaves(leafIds, e.target.checked)}
                    >
                        {record.title}
                    </Checkbox>
                );
            },
        },
        {
            title: t('wms.cutoff.columns.lastCutoffAt'),
            key: 'lastCutoffAt',
            width: 330,
            render: (_, record) =>
                record.isLeaf && record.lastCutoffAt
                    ? dayjs(Number(record.lastCutoffAt)).format('YYYY/MM/DD HH:mm:ss')
                    : '--',
        },
        {
            title: t('wms.cutoff.columns.planningStatus'),
            key: 'planningStatus',
            width: 140,
            render: (_, record) => (record.isLeaf ? renderPlanningStatus(record.planningStatus) : <span>--</span>),
        },
        // 末尾空白列吸收多余宽度，让上面三列紧凑靠左（否则名称列会撑满、后两列被推到最右）
        { key: 'spacer' },
    ];

    // 确认后在后台执行 cutoff：确认框立即关闭，执行期间顶部 Cutoff 按钮转 loading 防重复提交；不弹结果框，完成后 refetch，结果直接体现在每条线路的「规划状态」列
    const runCutoff = async (areaIds: number[]) => {
        if (!hubId) return;
        try {
            await executeCutoff({ variables: { hubId, areaIds } });
        } catch {
            // errorLink 对 VALIDATION/BUSINESS 类错误只 console 不弹提示，这里必须自行反馈，否则操作员会误以为执行成功
            message.error(t('wms.cutoff.executeFailed'));
            return;
        }
        setCheckedLeafIds(new Set());
        await refetch();
    };

    const handleCutoff = () => {
        if (!hubId || selectedAreaIds.length === 0) return;
        // 进行中的线路已在排线，不允许重复提交：命中则列出其 sorting_code 提示取消勾选，不进确认框
        const inProgressCodes = selectedAreaIds
            .filter((id) => areaMap.get(id)?.planningStatus === 'IN_PROGRESS')
            .map((id) => areaMap.get(id)?.sortingCode ?? `#${id}`);
        if (inProgressCodes.length > 0) {
            // 与 Cutoff 二次确认框一致的居中弹框（非顶部 toast）
            modal.warning({
                centered: true,
                width: 560,
                content: t('wms.cutoff.inProgressBlocked', { codes: inProgressCodes.join('、') }),
            });
            return;
        }
        const times = selectedAreaIds
            .map((id) => areaMap.get(id)?.lastCutoffAt)
            .filter((v): v is number => v != null);
        const latest = times.length ? Math.max(...times) : null;
        modal.confirm({
            title: t('wms.cutoff.confirm.title'),
            centered: true,
            width: 560,
            content: (
                <div>
                    <div>{t('wms.cutoff.confirm.selectedCount', { count: selectedAreaIds.length })}</div>
                    {latest && (
                        <div>
                            {t('wms.cutoff.confirm.lastCutoffAt')}:{' '}
                            <span className="whitespace-nowrap">
                                {dayjs(Number(latest)).format('YYYY/MM/DD HH:mm:ss')}
                            </span>
                        </div>
                    )}
                </div>
            ),
            // onOk 不返回 promise：确认框立即关闭、后台执行，结果看「规划状态」列；执行失败已在 runCutoff 内弹提示，此处 catch 只兜 refetch 等收尾异常
            onOk: () => {
                runCutoff(selectedAreaIds).catch(() => undefined);
            },
        });
    };

    return (
        <div>
            {/* 顶部操作条：sticky 吸顶，页面滚动时 Cutoff 按钮始终可见（滚动容器为 MainLayout 的 overflow-auto 内容区）*/}
            <div className="sticky top-0 z-20 flex justify-end bg-white py-2">
                <Button
                    type="primary"
                    disabled={selectedAreaIds.length === 0}
                    loading={executing}
                    onClick={handleCutoff}
                >
                    {t('wms.cutoff.buttons.cutoff')}
                </Button>
            </div>
            <Card>
                <Table<CutoffTreeNode>
                    // 未选中复选框边框也用主题按钮色(#722ed1)，与右上角 primary 按钮一致
                    className="[&_.ant-checkbox-inner]:!border-[#722ed1]"
                    rowKey="key"
                    columns={columns}
                    dataSource={treeData}
                    loading={loading}
                    pagination={false}
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        childrenColumnName: 'children',
                        indentSize: 60,
                    }}
                />
            </Card>
        </div>
    );
};

export default CutoffPage;
